import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, AlertTriangle, CheckCircle2, PieChart as PieIcon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';
import { Pie, Bars, Heatmap, money, moneyExact } from './PaymentReportCharts';
import RevenueIntelligence from './PaymentRevenueIntelligence';
import FinanceReports from './PaymentFinanceReports';
import ProfileAvatar from '../../profile/components/ProfileAvatar';

/**
 * Reports, in tabs, over one source.
 *
 * Every figure comes from the payment_revenue_line view, so the pie, the
 * drill-down and the gap report cannot disagree. They used to: the previous
 * screen combined getEarnings (order lines, programmes and tournaments only)
 * with SIGNED_LEDGER (platform-collected payments only) and showed $98.48 of a
 * real $390.48, and $0 of a real $219 outstanding.
 *
 * DATES ARE PAYMENT DATES throughout — money that arrived in the range. A
 * January enrolment settled in March belongs to March. The single exception is
 * the heatmap, which asks when sessions HAPPEN, and says so on the tab.
 *
 * OUTSTANDING IS NOT DATE-FILTERED. A debt from four months ago does not stop
 * existing because somebody picked "last 30 days".
 */

/**
 * How each stored payment method should read on screen.
 *
 * `card` and `stripe` are NOT the same thing and must never look it: stripe is
 * a card taken online, through Kynaix, and is owed back to the academy; card is
 * a card taken at the venue, which the academy already has. They appeared as
 * "card" and "Card (online)" — two names a glance reads as one.
 */
const METHOD_LABEL = {
    stripe:        'Card (online)',
    card:          'Card (at the venue)',
    cash:          'Cash',
    check:         'Cheque',
    venmo:         'Venmo',
    zelle:         'Zelle',
    bank_transfer: 'Bank transfer',
    other:         'Other',
    unknown:       'Not recorded',
};

/**
 * Sum the API's rows down to one row per label.
 *
 * The API groups by (bucket, subject_type) so a drill-down can ask "card, but
 * only for tournaments". A chart of ONE dimension has to add those back up
 * first, and this is what was missing: "How it was paid" listed Card (online)
 * three times ($180, $40, $45) and "Taken at the venue" three times, because
 * each method was split once per kind of thing sold. The totals were right —
 * they just arrived pre-shredded.
 *
 * The heatmap had the same input and a worse ending: it built its cells with
 * Object.fromEntries, so a second row for the same hour REPLACED the first
 * instead of adding to it, and quietly under-reported the hour.
 */
const mergeBy = (rows = [], labelOf = (r) => r.bucket) => {
    const totals = new Map();
    for (const r of rows) {
        const label = labelOf(r);
        totals.set(label, (totals.get(label) || 0) + (Number(r.collected) || 0));
    }
    return [...totals]
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);
};

const TABS = [
    { id: 'overview',  label: 'Overview',       hint: 'Where the money came from' },
    { id: 'offerings', label: 'Offerings',      hint: 'Revenue per programme, tournament, session or court' },
    { id: 'money',     label: 'How it was paid', hint: 'Method, and online versus at the desk' },
    { id: 'gaps',      label: 'Gaps',           hint: 'Booked but not billed, billed but not collected' },
    { id: 'checks',    label: 'Checks',         hint: 'Money that does not add up' },
    // The two screens that used to sit stacked below this one. Same page, same
    // toolbar, one place to look — three separate report blocks on one route was
    // three headings and three date pickers answering overlapping questions.
    { id: 'forecast',  label: 'What is coming', hint: 'Enrolments and sessions still to be paid for' },
    { id: 'statements', label: 'Statements',    hint: 'The ledger line by line, and payouts' },
];

/**
 * Ranges, of two kinds, and the difference matters.
 *
 * ROLLING windows ("last 30 days") answer "how are we doing lately". CALENDAR
 * ones ("this month", "this year") answer "what have we taken in the period we
 * actually report on" — which is the one somebody reconciling against a bank
 * statement or filing anything needs, and no combination of rolling days gives
 * it: on the 9th, "last 30 days" reaches back into the previous month and
 * quietly mixes two months' takings into one figure.
 *
 * `from` is a function so a calendar range is computed when it is clicked
 * rather than when this file loads — a tab left open overnight on New Year's
 * Eve would otherwise still be showing last year.
 */
const RANGES = [
    { id: 'mtd', label: 'This month',     from: () => { const d = new Date(); return isoDay(new Date(d.getFullYear(), d.getMonth(), 1)); } },
    { id: 'ytd', label: 'This year',      from: () => { const d = new Date(); return isoDay(new Date(d.getFullYear(), 0, 1)); } },
    { id: '30d', label: 'Last 30 days',   from: () => daysBack(30) },
    { id: '90d', label: 'Last 90 days',   from: () => daysBack(90) },
    { id: '12m', label: 'Last 12 months', from: () => daysBack(365) },
    { id: 'all', label: 'All time',       from: () => '' },
];

const isoDay = (d) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const daysBack = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };

const OFFERING_LABEL = {
    program: 'Programmes', tournament: 'Tournaments', session: 'Sessions',
    court: 'Court bookings', membership: 'Memberships',
};

/**
 * What each verdict means, and what it costs you to ignore it.
 *
 * Worded as the situation rather than the rule, and ordered by how much it
 * matters: money already taken for a place nobody holds is a person who will
 * arrive and be turned away, while an unpaid line for nobody is only an inflated
 * "owed" figure. Those two shared one label until now, which taught the reader
 * to skim past both.
 */
const VERDICT = {
    paid_without_entry:   { text: 'Paid, but nobody is registered',  hint: 'Money taken for a place nobody holds — refund it or enrol them', tone: 'text-red-600' },
    priced_but_unbilled:  { text: 'Registered, but never charged',   hint: 'Entries exist and no money was ever raised',                     tone: 'text-red-600' },
    entries_not_billed:   { text: 'Some entries never charged',      hint: 'More people registered than were billed',                       tone: 'text-red-600' },
    billed_without_entry: { text: 'Owed, but nobody is registered',  hint: 'A debt for nothing — cancel it or enrol them',                   tone: 'text-amber-600' },
    awaiting_payment:     { text: 'Awaiting payment',                hint: 'Billed correctly, not yet settled',                              tone: 'text-amber-600' },
    free:                 { text: 'Free',                            hint: '',                                                              tone: '' },
    ok:                   { text: 'OK',                              hint: '',                                                              tone: 'text-emerald-600' },
};

/**
 * The same verdict does not mean the same thing for a court.
 *
 * An unpaid programme place is money owed. An unpaid COURT booking is money
 * owed AND an hour taken off sale: the moment somebody holds it, nobody else
 * can book it, so a player who reached the payment step and walked away is
 * costing the academy the hour as well as the fee. That is a thing to chase
 * today, not a receivable to note — and "billed correctly, not yet settled"
 * does not say so.
 */
const COURT_VERDICT = {
    awaiting_payment: {
        text: 'Held but not paid',
        hint: 'The hour is off sale while it is unpaid — chase it or release it',
        tone: 'text-red-600',
    },
    priced_but_unbilled: {
        text: 'Hours given away',
        hint: 'The court was held and nothing was ever charged for it',
        tone: 'text-red-600',
    },
};

const verdictFor = (row) =>
    (row.subjectType === 'court' && COURT_VERDICT[row.verdict]) || VERDICT[row.verdict] || null;

const PaymentReportsTabs = ({ academyId }) => {
    const { theme } = useTheme();
    const [tab, setTab]     = useState('overview');
    const [rangeId, setRange] = useState('12m');
    const [from, setFrom]   = useState(() => daysBack(365));
    const [to, setTo]       = useState('');
    const [loading, setLoading] = useState(true);
    const [byType, setByType]   = useState(null);
    const [byOffering, setByOffering] = useState(null);
    const [byMethod, setByMethod] = useState(null);
    const [byWho, setByWho] = useState(null);
    const [health, setHealth] = useState(null);
    const [checks, setChecks] = useState(null);
    const [heat, setHeat]   = useState(null);
    // The offering breakdown. Null until a reader picks one: it is a per-offering
    // query and fetching all of them up front would be work nobody asked for.
    const [picked, setPicked] = useState(null);
    const [pickLoading, setPickLoading] = useState(false);

    const fetchOffering = useCallback(async (offering, page) => {
        setPickLoading(true);
        try {
            const res = await paymentsAPI.getOfferingTransactions({
                academyId, offering, from: from || undefined, to: to || undefined, page, pageSize: 10,
            });
            setPicked(res);
        } catch (err) {
            toast.error(err?.message || 'Could not load that offering');
            setPicked(null);
        } finally {
            setPickLoading(false);
        }
    }, [academyId, from, to]);

    const pickOffering = useCallback((row) => {
        // Clicking the open one closes it, so the same bar is the way in and out.
        if (picked?.offering === row.label) { setPicked(null); return; }
        fetchOffering(row.label, 1);
    }, [picked, fetchOffering]);

    const loadOfferingPage = useCallback(
        (page) => picked && fetchOffering(picked.offering, page),
        [picked, fetchOffering],
    );

    // A breakdown built for one date range must not linger over another: the
    // rows and the balance would be answering a question no longer on screen.
    useEffect(() => { setPicked(null); }, [academyId, from, to]);

    const load = useCallback(async () => {
        if (!academyId) return;
        setLoading(true);
        try {
            const range = { academyId, from: from || undefined, to: to || undefined };
            // Fetched together so every tab describes the same range: switching
            // tabs must never re-ask and quietly return different numbers.
            const [t, o, m, w, h, c, hour] = await Promise.all([
                paymentsAPI.getRevenueReport({ ...range, groupBy: 'subject_type' }),
                paymentsAPI.getRevenueReport({ ...range, groupBy: 'offering' }),
                paymentsAPI.getRevenueReport({ ...range, groupBy: 'method' }),
                paymentsAPI.getRevenueReport({ ...range, groupBy: 'collected_by' }),
                paymentsAPI.getOfferingHealth({ academyId }),
                paymentsAPI.getReconciliation({ academyId }),
                paymentsAPI.getRevenueReport({ ...range, groupBy: 'hour' }),
            ]);
            setByType(t); setByOffering(o); setByMethod(m); setByWho(w);
            setHealth(h); setChecks(c); setHeat(hour);
        } catch (err) {
            toast.error(err?.message || 'Could not load reports');
        } finally {
            setLoading(false);
        }
    }, [academyId, from, to]);

    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => {
        const collected = (byType?.buckets || []).reduce((s, b) => s + b.collected, 0);
        const refunded  = (byType?.buckets || []).reduce((s, b) => s + b.refunded, 0);
        const outstanding = (byType?.outstanding || []).reduce((s, b) => s + b.outstanding, 0);
        return { collected, refunded, outstanding, net: collected - refunded };
    }, [byType]);

    // Each chart's rows, built once so the bars and the total beside them can
    // never be computed from different arrays.
    const typeRows     = useMemo(() => mergeBy(byType?.buckets, b => OFFERING_LABEL[b.bucket] || b.bucket), [byType]);
    const owedRows      = useMemo(() => (byType?.outstanding || []).map(o => ({
        label: OFFERING_LABEL[o.subjectType] || o.subjectType, value: o.outstanding,
    })), [byType]);
    const offeringRows = useMemo(() => mergeBy(byOffering?.buckets), [byOffering]);
    // Offerings with money owed but none collected: absent from the pie by
    // definition, and the first thing somebody asks about when they are.
    const owedOnly = useMemo(() => {
        const earned = new Set(typeRows.map(r => r.label));
        return owedRows.filter(r => !earned.has(r.label) && r.value > 0);
    }, [typeRows, owedRows]);
    const methodRows   = useMemo(() => mergeBy(byMethod?.buckets, b => METHOD_LABEL[b.bucket] || b.bucket), [byMethod]);
    // Three answers, not two. An online card payment routed straight to the
    // host's own Stripe account is neither "collected by Kynaix" nor "taken at
    // the venue" — nobody stood at a desk, and Kynaix is holding nothing.
    const WHO_LABEL = {
        platform:    'Collected by Kynaix',
        host_direct: 'Paid straight to you',
        host_desk:   'Taken at the venue',
    };
    const whoRows      = useMemo(() => mergeBy(byWho?.buckets,
        b => WHO_LABEL[b.bucket] || b.bucket), [byWho]);   // eslint-disable-line react-hooks/exhaustive-deps
    // Rows with no hour are programmes and tournaments — things with a season,
    // not a start time. They were landing in an "unknown" column that outweighed
    // every real hour on a chart that only claims to be about sessions.
    const hourRows     = useMemo(() => mergeBy((heat?.buckets || []).filter(b => b.bucket !== 'unknown')), [heat]);

    const input = `px-3 py-2 rounded-lg border ${theme.border} ${theme.card} ${theme.text} text-sm`;
    const card  = `${theme.card} rounded-xl p-4`;

    /**
     * A chart's heading, with that chart's OWN total beside it.
     *
     * The total is summed from the rows actually drawn, not passed in, so it
     * cannot drift from the bars underneath it. Its job is to be checked
     * against Collected at the top of the page: two boxes cutting the same
     * $519 different ways must both still say $519, and any box that does not
     * is either measuring something narrower — which it should say — or wrong.
     * The duplicate-row bug survived this long because nothing on the page
     * added up to anything.
     */
    const Head = ({ title, rows, note, scope }) => (
        <div className="mb-3">
            <div className="flex items-baseline justify-between gap-3">
                <p className={`text-sm font-semibold ${theme.text}`}>{title}</p>
                <span className={`text-sm font-semibold tabular-nums ${theme.text}`}>
                    {money(rows.reduce((s, r) => s + (Number(r.value) || 0), 0))}
                </span>
            </div>
            {(note || scope) && (
                <p className={`text-[11px] ${theme.textMuted} mt-1`}>
                    {scope && <span className="font-medium">{scope} · </span>}{note}
                </p>
            )}
        </div>
    );

    return (
        <div className="space-y-4">
            <div>
                <h2 className={`text-xl font-bold ${theme.text}`}>Reports</h2>
                <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
                    Every figure comes from the same source, dated by when the money arrived.
                </p>
            </div>

            {/* One toolbar, fixed shape: shortcuts write into the two dates and
                which one looks selected is derived, so the screen cannot claim
                "last 30 days" while the inputs say otherwise. */}
            <div className={`${theme.card} rounded-xl p-3 space-y-3`}>
                <div className={`inline-flex rounded-lg border ${theme.border} p-0.5 overflow-x-auto max-w-full`}>
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            title={t.hint}
                            data-testid={`report-tab-${t.id}`}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                                tab === t.id ? theme.tabActive : `${theme.textMuted} ${theme.cardHover}`
                            }`}
                        >
                            {t.label}
                            {t.id === 'gaps' && health?.counts?.problems > 0 && (
                                <span className="ml-1.5 text-amber-500">{health.counts.problems}</span>
                            )}
                            {t.id === 'checks' && checks && !checks.clean && (
                                <span className="ml-1.5 text-red-500">{checks.anomalies.length}</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <div className={`inline-flex rounded-lg border ${theme.border} p-0.5`}>
                        {RANGES.map(r => {
                            const active = rangeId === r.id;
                            return (
                                <button
                                    key={r.id}
                                    onClick={() => { setRange(r.id); setFrom(r.from()); setTo(''); }}
                                    data-testid={`report-range-${r.id}`}
                                    className={`px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                                        active ? theme.tabActive : `${theme.textMuted} ${theme.cardHover}`
                                    }`}
                                >
                                    {r.label}
                                </button>
                            );
                        })}
                    </div>
                    <input type="date" value={from} onChange={e => { setFrom(e.target.value); setRange('custom'); }}
                           aria-label="From date" data-testid="report-from" className={input} />
                    <span className={`text-xs ${theme.textMuted}`}>to</span>
                    <input type="date" value={to} onChange={e => { setTo(e.target.value); setRange('custom'); }}
                           aria-label="To date" data-testid="report-to" className={input} />
                    <p className={`text-xs ${theme.textMuted}`}>
                        {loading ? 'Loading…' : TABS.find(t => t.id === tab)?.hint}
                    </p>
                </div>
            </div>

            {/* Four figures, always the same four, so the eye learns where to
                look. Outstanding is deliberately not date-filtered. */}
            <div className="grid gap-3 sm:grid-cols-4">
                {[
                    ['Collected',   totals.collected,   theme.text],
                    ['Refunded',    totals.refunded,    'text-amber-600'],
                    ['Net',         totals.net,         theme.text],
                    ['Outstanding', totals.outstanding, totals.outstanding > 0 ? 'text-amber-600' : theme.textMuted],
                ].map(([label, value, tone]) => (
                    <div key={label} className={card}>
                        <p className={`text-xs ${theme.textMuted}`}>{label}</p>
                        <p className={`text-2xl font-semibold tabular-nums ${tone}`}>{money(value)}</p>
                        {label === 'Outstanding' && (
                            <p className={`text-[11px] ${theme.textMuted} mt-0.5`}>all time, not this range</p>
                        )}
                    </div>
                ))}
            </div>

            <div className={`relative min-h-[18rem] transition-opacity ${loading ? 'opacity-40' : ''}`}>
                {tab === 'overview' && (
                    <div className="grid gap-3 lg:grid-cols-2">
                        <div className={card}>
                            {/* The pie carries its own total in the hole, so this
                                heading keeps the icon and skips a second copy. */}
                            <p className={`text-sm font-semibold ${theme.text} mb-3 flex items-center gap-1.5`}>
                                <PieIcon size={14} /> Revenue by offering
                            </p>
                            <Pie data={typeRows} />
                            {/* WHAT THE PIE CANNOT SHOW.
                                It charts money that arrived, so an offering that
                                has sold plenty and collected nothing has no
                                slice — and its absence reads as "we sell none of
                                those" rather than "none of it is paid for". Said
                                here rather than left as an inference. */}
                            {owedOnly.length > 0 && (
                                <p className={`text-xs ${theme.textMuted} mt-3 pt-3 border-t ${theme.border}`}>
                                    Not shown — nothing collected yet:{' '}
                                    {owedOnly.map(o => `${o.label} (${money(o.value)} owed)`).join(', ')}
                                </p>
                            )}
                        </div>
                        <div className={card}>
                            <Head
                                title="Still owed, by offering"
                                rows={owedRows}
                                scope="all time"
                                note="Debts do not expire with the date range, so this ignores it."
                            />
                            {owedRows.length ? (
                                <Bars data={owedRows} />
                            ) : (
                                <p className={`text-sm ${theme.textMuted} py-6 text-center`}>
                                    Nothing outstanding — everything booked has been paid for.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {tab === 'offerings' && (
                    <div className="grid gap-3 lg:grid-cols-2">
                        <div className={card}>
                            <Head title="Revenue by offering" rows={offeringRows} />
                            <Bars data={offeringRows} onPick={pickOffering} picked={picked?.offering} />
                            <p className={`text-xs ${theme.textMuted} mt-3 flex items-center gap-1.5`}>
                                {pickLoading && <Loader2 size={12} className="animate-spin" />}
                                {pickLoading ? 'Loading breakdown…' : 'Pick an offering to see what makes up its total.'}
                            </p>
                        </div>
                        <div className={card}>
                            <Head
                                title="When sessions earn"
                                rows={hourRows}
                                scope="sessions only"
                                note="By the session's own clock, not the payment date — this is the one place the two differ. Programmes and tournaments have no start time, so they are not counted here."
                            />
                            {/* One row, honestly labelled. This asked the API for
                                revenue by HOUR and then filed every hour under
                                "Mon", inventing a weekday the data never carried.
                                A real grid needs hour AND weekday from one query;
                                until then, say "all days" rather than name a day
                                that means nothing. */}
                            <Heatmap
                                days={['All days']}
                                cells={Object.fromEntries(hourRows.map(r => [`All days|${r.label}`, r.value]))}
                            />
                        </div>

                        {/* The breakdown, which only exists once someone has asked
                            for it. Spans both columns: these are wide rows and
                            squeezing them beside the chart would wrap every one. */}
                        {picked && (
                            <div className={`${card} lg:col-span-2`}>
                                <OfferingBreakdown
                                    picked={picked}
                                    onClose={() => setPicked(null)}
                                    onPage={loadOfferingPage}
                                />
                            </div>
                        )}
                    </div>
                )}

                {tab === 'money' && (
                    <div className="grid gap-3 lg:grid-cols-2">
                        <div className={card}>
                            <Head title="By payment method" rows={methodRows} />
                            <Bars data={methodRows} />
                        </div>
                        <div className={card}>
                            <Head
                                title="Who took the money"
                                rows={whoRows}
                                note="Collected by Kynaix is owed back to you. Paid straight to you went to your own Stripe account. Taken at the venue is cash and the like, which you already have."
                            />
                            <Bars data={whoRows} />
                        </div>
                    </div>
                )}

                {tab === 'gaps' && (
                    <div className={card}>
                        <div className="flex items-center justify-between mb-3">
                            <p className={`text-sm font-semibold ${theme.text}`}>
                                Booked, billed, collected
                            </p>
                            <p className={`text-xs ${theme.textMuted}`}>
                                {health?.counts?.free || 0} free offerings excluded
                            </p>
                        </div>
                        {(health?.offerings || []).length === 0 ? (
                            <p className={`text-sm ${theme.textMuted} py-8 text-center flex items-center justify-center gap-2`}>
                                <CheckCircle2 size={16} className="text-emerald-600" />
                                Every priced offering is billed and settled.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className={`text-left text-[10px] uppercase tracking-wide ${theme.textMuted}`}>
                                            <th className="pb-2">Offering</th><th>Price</th><th>Booked</th>
                                            <th>Billed</th><th>Collected</th><th>Owed</th><th>Verdict</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {health.offerings.map(o => (
                                            <tr key={`${o.subjectType}-${o.subjectId}`} className={`border-t ${theme.border}`}>
                                                <td className={`py-2 ${theme.text}`}>
                                                    {o.name}
                                                    <span className={`block text-[10px] ${theme.textMuted}`}>
                                                        {OFFERING_LABEL[o.subjectType] || o.subjectType}
                                                    </span>
                                                </td>
                                                <td className="tabular-nums">{money(o.price)}</td>
                                                <td className="tabular-nums">{o.booked}</td>
                                                <td className="tabular-nums">{o.billedLines} · {money(o.billed)}</td>
                                                <td className="tabular-nums">{money(o.collected)}</td>
                                                <td className="tabular-nums">{money(o.outstanding)}</td>
                                                <td className={`text-xs font-medium ${verdictFor(o)?.tone || ''}`}>
                                                    {verdictFor(o)?.text || o.verdict}
                                                    {verdictFor(o)?.hint && (
                                                        <span className={`block font-normal ${theme.textMuted}`}>
                                                            {verdictFor(o).hint}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {tab === 'checks' && (
                    <div className={card}>
                        <p className={`text-sm font-semibold ${theme.text} mb-1`}>Money that does not add up</p>
                        <p className={`text-[11px] ${theme.textMuted} mb-3`}>
                            This page is meant to be empty. Every row is something a person has to explain.
                        </p>
                        {checks?.clean ? (
                            <p className={`text-sm ${theme.textMuted} py-8 text-center flex items-center justify-center gap-2`}>
                                <CheckCircle2 size={16} className="text-emerald-600" /> Everything reconciles.
                            </p>
                        ) : (
                            <ul className="space-y-2">
                                {(checks?.anomalies || []).map(a => (
                                    <li key={a.anomaly} className={`border ${theme.border} rounded-lg p-3`}>
                                        <p className={`text-sm font-medium ${theme.text} flex items-center gap-1.5`}>
                                            <AlertTriangle size={13} className="text-amber-600" />
                                            {a.anomaly.replace(/_/g, ' ')}
                                            <span className={`ml-auto tabular-nums ${theme.textMuted}`}>
                                                {a.count} · {money(a.amount)}
                                            </span>
                                        </p>
                                        <p className={`text-[11px] ${theme.textMuted} mt-1 truncate`}>
                                            {a.examples.map(e => e.orderNumber || e.ref).join(', ')}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {/* Rendered inside the tab strip rather than stacked below it.
                    They carry their own headings and ranges, which is why they
                    are given the width and left alone. */}
                {tab === 'forecast'   && <RevenueIntelligence academyId={academyId} />}
                {tab === 'statements' && <FinanceReports ownerType="academy" ownerId={academyId} />}

                {loading && !byType && (
                    <div className={`absolute inset-0 flex items-center justify-center gap-2 ${theme.textMuted}`}>
                        <Loader2 size={18} className="animate-spin" /> <span className="text-sm">Loading…</span>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * WHAT ONE OFFERING'S TOTAL IS MADE OF.
 *
 * The summary answers "how did this number happen" — what customers handed
 * over, what Kynaix kept, what the academy is credited, and what survives the
 * refunds. The transactions answer "which payments were they", and are only
 * fetched when somebody asks: an academy reading its totals usually does not
 * want two hundred rows underneath them.
 *
 * ONE ROW PER MOVEMENT, SIGNED. A payment and its refund are two rows, not one
 * row amended — the same way Stripe's ledger and payment_ledger both keep it.
 * The balance is the running total after each, computed over the whole set on
 * the server, so page two continues page one instead of restarting at zero.
 */
function OfferingBreakdown({ picked, onClose, onPage }) {
    const { theme } = useTheme();
    const [showTx, setShowTx] = useState(false);
    const s = picked.summary;

    const th = `text-left text-xs font-semibold ${theme.textSecondary} px-3 py-2 whitespace-nowrap`;
    const td = `text-sm ${theme.text} px-3 py-2 whitespace-nowrap`;
    const num = `${td} text-right tabular-nums`;

    // Money that moved the wrong way reads as a loss, not as a smaller gain.
    const signed = (v) => (
        <span className={v < 0 ? 'text-pink-600 dark:text-pink-400' : undefined}>
            {v < 0 ? `−${moneyExact(Math.abs(v))}` : moneyExact(v)}
        </span>
    );

    const when = (iso) => {
        const d = new Date(iso);
        return {
            date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
            time: d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
        };
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <h4 className={`text-base font-semibold ${theme.text} truncate`}>{picked.offering}</h4>
                    <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                        Accepted is everything taken; net is what is left after refunds.
                        The platform fee is {' '}
                        <strong className={theme.text}>Kynaix&rsquo;s cut</strong>, not Stripe&rsquo;s.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className={`text-xs px-2 py-1 rounded border ${theme.border} ${theme.textSecondary} ${theme.cardHover} flex-shrink-0`}
                >
                    Close
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[46rem]">
                    <thead>
                        <tr className={`border-b ${theme.border}`}>
                            <th className={th}>Offering</th>
                            <th className={`${th} text-right`}>Bookings</th>
                            <th className={`${th} text-right`}>Accepted</th>
                            <th className={`${th} text-right`}>Fee deducted</th>
                            <th className={`${th} text-right`}>Real credit</th>
                            <th className={`${th} text-right`}>Refunded</th>
                            <th className={`${th} text-right`}>Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className={td}>{picked.offering}</td>
                            <td className={num}>{s.bookings}</td>
                            <td className={num}>{moneyExact(s.acceptedAmount)}</td>
                            <td className={num}>{moneyExact(s.feeDeducted)}</td>
                            <td className={num}>{moneyExact(s.realCredit)}</td>
                            <td className={num}>
                                {s.refunded ? signed(-s.refunded) : <span className={theme.textMuted}>—</span>}
                            </td>
                            <td className={`${num} font-semibold`}>{signed(s.net)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                <button
                    type="button"
                    onClick={() => setShowTx(v => !v)}
                    className={`text-xs font-semibold ${theme.link} hover:underline`}
                >
                    {showTx ? 'Hide transactions' : `Show transactions (${picked.total})`}
                </button>
            </div>

            {showTx && (
                <>
                    <div className="overflow-x-auto mt-3">
                        <table className="w-full min-w-[52rem]">
                            <thead>
                                <tr className={`border-b ${theme.border}`}>
                                    <th className={th}>Date</th>
                                    <th className={th}>Time</th>
                                    <th className={th}>Event</th>
                                    <th className={th}>Customer</th>
                                    <th className={th}>Order</th>
                                    <th className={`${th} text-right`}>Customer paid</th>
                                    <th className={`${th} text-right`}>Cost per booking</th>
                                    <th className={`${th} text-right`}>Platform fee</th>
                                    <th className={`${th} text-right`}>Revenue</th>
                                    <th className={`${th} text-right`}>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {picked.transactions.map(t => {
                                    const w = when(t.occurredAt);
                                    return (
                                        <tr key={t.id} className={`border-b ${theme.border} last:border-0`}>
                                            <td className={td}>{w.date}</td>
                                            <td className={`${td} ${theme.textSecondary}`}>{w.time}</td>
                                            {/* WHICH occurrence, not when it was paid
                                                for. Every court booking carries the
                                                same name, so the slot's own clock is
                                                the only thing that separates them. */}
                                            <td className={`${td} ${theme.textSecondary} text-xs`}>
                                                {t.occursAt
                                                    ? `${when(t.occursAt).date} · ${when(t.occursAt).time}`
                                                    : <span className={theme.textMuted}>—</span>}
                                            </td>
                                            {/* A face and a name. An address identifies
                                                an account; a venue recognises a person. */}
                                            <td className={td}>
                                                {t.customer?.name || t.customer?.email ? (
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <ProfileAvatar
                                                            userId={t.customer.id}
                                                            profileImage={t.customer.profileImage}
                                                            firstName={(t.customer.name || '').split(' ')[0]}
                                                            lastName={(t.customer.name || '').split(' ').slice(1).join(' ')}
                                                            size="xs"
                                                        />
                                                        <span className="truncate max-w-[11rem]" title={t.customer.email || undefined}>
                                                            {t.customer.name || t.customer.email}
                                                        </span>
                                                    </div>
                                                ) : <span className={theme.textMuted}>—</span>}
                                            </td>
                                            <td className={`${td} ${theme.textSecondary} text-xs`}>{t.orderNumber}</td>
                                            <td className={num}>{signed(t.customerPaid)}</td>
                                            {/* What the booking costs, and what Kynaix
                                                adds to it. The two add up to Customer
                                                paid, so the row explains itself without
                                                the reader holding a rate in their head. */}
                                            <td
                                                className={`${num} ${theme.textSecondary} text-xs`}
                                                title={t.feeRate
                                                    ? `${moneyExact(t.feeRate.base)} booking + platform fee of ${t.feeRate.percent}% `
                                                      + `(${moneyExact(t.feeRate.percentAmount)}) plus ${moneyExact(t.feeRate.fixed)} flat `
                                                      + `= ${moneyExact(t.feeRate.base + Math.abs(t.platformFee))}`
                                                    : undefined}
                                            >
                                                {t.feeRate
                                                    ? <>{moneyExact(t.feeRate.base)} <span className={theme.textMuted}>+</span> {moneyExact(Math.abs(t.platformFee))}</>
                                                    : <span className={theme.textMuted}>—</span>}
                                            </td>
                                            <td className={num}>{signed(t.platformFee)}</td>
                                            <td className={num}>{signed(t.revenue)}</td>
                                            <td className={`${num} font-semibold`}>{moneyExact(t.balance)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {picked.pages > 1 && (
                        <div className="flex items-center justify-between mt-3">
                            <span className={`text-xs ${theme.textMuted}`}>
                                Page {picked.page} of {picked.pages} · {picked.total} movements
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    disabled={picked.page <= 1}
                                    onClick={() => onPage(picked.page - 1)}
                                    className={`text-xs px-3 py-1 rounded border ${theme.border} ${
                                        picked.page <= 1 ? 'opacity-40 cursor-not-allowed' : `${theme.link} ${theme.cardHover}`
                                    }`}
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    disabled={picked.page >= picked.pages}
                                    onClick={() => onPage(picked.page + 1)}
                                    className={`text-xs px-3 py-1 rounded border ${theme.border} ${
                                        picked.page >= picked.pages ? 'opacity-40 cursor-not-allowed' : `${theme.link} ${theme.cardHover}`
                                    }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default PaymentReportsTabs;
